import React, { createContext, useContext, useState, useCallback, PropsWithChildren, useEffect } from "react";
import { useSearchQuery } from "./search_query_provider";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Types

type DialogStateContextType = {
  isOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  hasComponentMounted: (componentId: string) => boolean;
  registerComponentMount: (componentId: string) => void;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Context Provider

const DialogStateContext = createContext<DialogStateContextType | undefined>(undefined);

export function DialogStateProvider({ children }: PropsWithChildren) {
  const { setQuery } = useSearchQuery();
  const [isOpen, setIsOpen] = useState(false);
  const [mountedComponents, setMountedComponents] = useState<Set<string>>(new Set());

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // State Management

  const openDialog = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    // Reset mounted components when dialog closes
    setMountedComponents(new Set());
    // Reset the search query
    setQuery("");
  }, [setQuery]);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Component Mounted Tracking
  // ---
  // This allows us to track which components have mounted
  // since the Dialog was opened, and allow us to do things
  // once per "search session".

  const hasComponentMounted = useCallback(
    (componentId: string) => {
      return mountedComponents.has(componentId);
    },
    [mountedComponents],
  );

  const registerComponentMount = useCallback((componentId: string) => {
    setMountedComponents((existing) => {
      const updated = new Set(existing);
      updated.add(componentId);
      return updated;
    });
  }, []);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Rendering

  const value = {
    isOpen,
    openDialog,
    closeDialog,
    hasComponentMounted,
    registerComponentMount,
  };

  return <DialogStateContext.Provider value={value}>{children}</DialogStateContext.Provider>;
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Hooks

export function useDialogState() {
  const context = useContext(DialogStateContext);
  if (context === undefined) {
    throw new Error("useDialogState must be used within a DialogStateProvider");
  }
  return context;
}

export function useDialogMount(componentId: string) {
  const { isOpen, hasComponentMounted, registerComponentMount } = useDialogState();

  useEffect(() => {
    if (isOpen) {
      registerComponentMount(componentId);
    }
  }, [isOpen, componentId, registerComponentMount]);

  return hasComponentMounted(componentId);
}
